import React, { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  Globe, Save, Loader2, Plus, X, ExternalLink, Eye, Image as ImageIcon,
  Edit3, Trash2, Sparkles, AlertCircle, Link as LinkIcon, Check,
} from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import ImageUploader from "@/components/uploads/ImageUploader";
import api, { mediaUrl } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import { useAuth } from "@/contexts/AuthContext";
import { usePortfolioMaxItems } from "@/lib/limits";
import { formatTime } from "@/lib/dates";

export default function MasterLandingEditor() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  // Every plan gets the same page (PLANES, 2026-09-30): slogan, description
  // and a portfolio of `portfolio_max_items`, which the server owns.
  const portfolioLimit = usePortfolioMaxItems() ?? 0;

  const [slogan, setSlogan] = useState(user?.profile?.landing_slogan || "");
  const [bio, setBio] = useState(user?.profile?.landing_bio || "");
  const [businessLogo, setBusinessLogo] = useState(user?.profile?.business_logo_url || "");
  const [items, setItems] = useState(user?.profile?.portfolio_items || []);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setSlogan(user?.profile?.landing_slogan || "");
    setBio(user?.profile?.landing_bio || "");
    setBusinessLogo(user?.profile?.business_logo_url || "");
    setItems(user?.profile?.portfolio_items || []);
  }, [user]);

  const saveCopy = async () => {
    setError("");
    setSaving(true);
    try {
      const payload = { landing_bio: bio, business_logo_url: businessLogo, landing_slogan: slogan };
      await api.patch("/users/me", payload);
      if (refresh) await refresh();
      setSavedAt(new Date());
    } catch (e) {
      setError(errorMessage(e, t("common.error")));
    } finally {
      setSaving(false);
    }
  };

  const slug = user?.profile?.public_slug;
  const publicUrl = slug ? `${window.location.origin}/pro/${slug}` : null;

  const copyPublicLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt(t("webEditor.copyLink"), publicUrl);
    }
  };

  const subtitleText = portfolioLimit > 0
    ? t("webEditor.subtitle", { portfolio: t("webEditor.subtitlePort", { n: portfolioLimit, kind: portfolioLimit === 1 ? "piece" : "pieces" }) })
    : t("webEditor.subtitle", { portfolio: "" });

  return (
    <DashboardLayout role="provider">
      <div data-testid="master-landing-editor">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-worker flex items-center gap-2">
              <Globe className="text-hive-ink" /> {t("webEditor.title")}
            </h1>
            <p className="mt-2 text-smoke">{subtitleText}</p>
          </div>
          {slug && (
            <div className="flex gap-2 flex-wrap">
              <button onClick={copyPublicLink} className="fx-btn fx-btn--secondary text-sm" data-testid="copy-public-link">
                {copied ? (<><Check size={14} /> {t("webEditor.linkCopied")}</>) : (<><LinkIcon size={14} /> {t("webEditor.copyLink")}</>)}
              </button>
              <a href={`/pro/${slug}`} target="_blank" rel="noopener noreferrer" className="fx-btn fx-btn--secondary text-sm" data-testid="preview-public-btn">
                <Eye size={14} /> {t("webEditor.preview")}
              </a>
            </div>
          )}
        </div>

        <div className="card-base p-5 mb-6 bg-linen border border-sand" data-testid="editor-help">
          <p className="font-display font-bold text-worker text-sm flex items-center gap-2">
            <Sparkles size={14} className="text-hive-ink" /> {t("webEditor.help.title")}
          </p>
          <ol className="mt-2 text-sm text-smoke space-y-1 list-decimal list-inside leading-relaxed">
            <li><Trans i18nKey="webEditor.help.step1Logo" components={{ strong: <strong className="text-worker" /> }} /></li>
            <li><Trans i18nKey="webEditor.help.step1Pro" components={{ strong: <strong className="text-worker" /> }} /></li>
            <li><Trans i18nKey="webEditor.help.step2" components={{ em: <em /> }} /></li>
            {portfolioLimit > 0 && <li><Trans i18nKey="webEditor.help.step3" values={{ n: portfolioLimit }} components={{ strong: <strong className="text-worker" />, em: <em /> }} /></li>}
            <li><Trans i18nKey="webEditor.help.step4" components={{ strong: <strong className="text-worker" /> }} /></li>
          </ol>
        </div>

        <div className="card-base p-6 mb-6" data-testid="brand-editor">
          <h2 className="font-display text-xl font-bold text-worker mb-1">{t("webEditor.logo.heading")}</h2>
          <p className="text-sm text-smoke mb-4">{t("webEditor.logo.hint")}</p>
          <div className="max-w-[180px]">
            <ImageUploader
              value={businessLogo}
              purpose="landing_logo"
              label={t("webEditor.logo.upload")}
              aspect="square"
              testid="business-logo"
              onUploaded={({ url }) => setBusinessLogo(url)}
              onCleared={() => setBusinessLogo("")}
            />
          </div>
        </div>

        <div className="card-base p-6 mb-6" data-testid="copy-editor">
          <h2 className="font-display text-xl font-bold text-worker mb-4">{t("webEditor.copy.heading")}</h2>
          <div className="space-y-4">
            <div>
              <label className="label-base">{t("webEditor.copy.slogan")}</label>
              <input
                value={slogan}
                onChange={(e) => setSlogan(e.target.value)}
                className="input-base"
                placeholder={t("webEditor.copy.sloganPlaceholder")}
                maxLength={140}
                data-testid="slogan-input"
              />
              <p className="text-xs text-smoke mt-1">{t("webEditor.copy.sloganHint", { n: slogan.length })}</p>
            </div>
            <div>
              <label className="label-base">{t("webEditor.copy.services")}</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={5}
                className="input-base resize-none"
                placeholder={t("webEditor.copy.servicesPlaceholder")}
                maxLength={800}
                data-testid="bio-input"
              />
              <p className="text-xs text-smoke mt-1">{bio.length}/800</p>
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-sm text-red-700" data-testid="copy-error">
                <AlertCircle size={14} className="inline mr-1" /> {error}
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              {savedAt && <span className="text-xs text-smoke">{t("common.savedSuccess")} {formatTime(savedAt)}</span>}
              <button onClick={saveCopy} disabled={saving} className="fx-btn fx-btn--primary" data-testid="save-copy-btn">
                {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                {t("webEditor.copy.save")}
              </button>
            </div>
          </div>
        </div>

        <div className={`card-base p-6 ${portfolioLimit <= 0 ? "opacity-60 pointer-events-none" : ""}`} data-testid="portfolio-section">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <h2 className="font-display text-xl font-bold text-worker">{t("webEditor.portfolio.heading")}</h2>
              <p className="text-sm text-smoke">{t("webEditor.portfolio.counter", { used: items.length, max: portfolioLimit })}</p>
            </div>
            <button
              onClick={() => setAdding(true)}
              disabled={portfolioLimit <= 0 || items.length >= portfolioLimit}
              className="fx-btn fx-btn--secondary text-sm"
              data-testid="add-portfolio-btn"
            >
              <Plus size={14} /> {t("webEditor.portfolio.add")}
            </button>
          </div>

          {items.length === 0 ? (
            <div className="border-2 border-dashed border-sand rounded-md p-10 text-center text-smoke" data-testid="empty-portfolio">
              <ImageIcon size={36} className="mx-auto mb-2 text-hive-ink" />
              <p className="font-display font-semibold text-worker">{t("dashboard.client.emptyTitle")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((it) => (
                <PortfolioCard
                  key={it.item_id}
                  t={t}
                  item={it}
                  onEdit={() => setEditing(it)}
                  onDelete={async () => {
                    await api.delete(`/users/me/portfolio/${it.item_id}`);
                    if (refresh) await refresh();
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {(adding || editing) && (
        <ItemModal
          t={t}
          item={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={async () => {
            setAdding(false);
            setEditing(null);
            if (refresh) await refresh();
          }}
        />
      )}
    </DashboardLayout>
  );
}

function PortfolioCard({ t, item, onEdit, onDelete }) {
  return (
    <div className="card-base overflow-hidden" data-testid={`portfolio-card-${item.item_id}`}>
      <div className="aspect-video bg-sand relative overflow-hidden">
        <img src={mediaUrl(item.image_url)} alt={item.title} className="w-full h-full object-cover"
          onError={(e) => { e.target.style.display = "none"; }} />
      </div>
      <div className="p-4">
        <p className="font-display font-bold text-worker line-clamp-1">{item.title}</p>
        {item.description && <p className="text-sm text-smoke mt-1 line-clamp-2">{item.description}</p>}
        <div className="mt-3 flex items-center gap-2">
          <button onClick={onEdit} className="fx-btn fx-btn--secondary text-xs" data-testid={`edit-${item.item_id}`}>
            <Edit3 size={12} /> {t("webEditor.portfolio.edit")}
          </button>
          <button onClick={onDelete} className="fx-btn fx-btn--secondary text-xs border-red-300 text-red-700 hover:bg-red-50" data-testid={`delete-${item.item_id}`}>
            <Trash2 size={12} /> {t("webEditor.portfolio.remove")}
          </button>
          {item.project_url && (
            <a href={item.project_url} target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-hive-ink hover:text-worker inline-flex items-center gap-1">
              <ExternalLink size={10} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function ItemModal({ t, item, onClose, onSaved }) {
  const isEdit = !!item;
  const [image_url, setImageUrl] = useState(item?.image_url || "");
  const [title, setTitle] = useState(item?.title || "");
  const [description, setDescription] = useState(item?.description || "");
  const [project_url, setProjectUrl] = useState(item?.project_url || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!image_url || title.length < 2) {
      setError(t("webEditor.portfolio.modal.errorRequired"));
      return;
    }
    setBusy(true);
    try {
      const body = { image_url, title, description, project_url: project_url || null };
      if (isEdit) await api.patch(`/users/me/portfolio/${item.item_id}`, body);
      else await api.post("/users/me/portfolio", body);
      onSaved();
    } catch (e) {
      setError(errorMessage(e, t("common.error")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-worker/70 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" data-testid="portfolio-modal">
      <div className="bg-cream rounded-md max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-smoke hover:text-worker"><X size={20} /></button>
        <h3 className="font-display text-2xl font-bold text-worker mb-1">
          {isEdit ? t("webEditor.portfolio.modal.editTitle") : t("webEditor.portfolio.modal.addTitle")}
        </h3>
        <p className="text-sm text-smoke mb-5">{t("webEditor.portfolio.modal.subtitle")}</p>

        <div className="space-y-4">
          <div>
            <label className="label-base">{t("webEditor.portfolio.modal.photo")}</label>
            <ImageUploader
              value={image_url}
              purpose="landing_portfolio"
              label={t("common.uploadPhoto")}
              aspect="wide"
              testid="portfolio-image"
              onUploaded={({ url }) => setImageUrl(url)}
              onCleared={() => setImageUrl("")}
            />
          </div>
          <div>
            <label className="label-base">{t("webEditor.portfolio.modal.title")}</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="input-base"
              placeholder={t("webEditor.portfolio.modal.titlePlaceholder")} maxLength={80} data-testid="title-input" />
          </div>
          <div>
            <label className="label-base">{t("webEditor.portfolio.modal.description")}</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
              className="input-base resize-none" placeholder={t("webEditor.portfolio.modal.descriptionPlaceholder")} maxLength={300} data-testid="description-input" />
          </div>
          <div>
            <label className="label-base">{t("webEditor.portfolio.modal.projectUrl")} <span className="text-smoke normal-case">{t("webEditor.portfolio.modal.projectUrlOptional")}</span></label>
            <input value={project_url} onChange={(e) => setProjectUrl(e.target.value)} className="input-base font-mono text-sm" placeholder="https://..." maxLength={500} data-testid="project-url-input" />
          </div>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-sm text-red-700" data-testid="modal-error">
              <AlertCircle size={14} className="inline mr-1" /> {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="fx-btn fx-btn--secondary">{t("common.cancel")}</button>
            <button onClick={submit} disabled={busy} className="fx-btn fx-btn--primary" data-testid="submit-item-btn">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
              {isEdit ? t("webEditor.portfolio.modal.ctaUpdate") : t("webEditor.portfolio.modal.ctaAdd")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
